import React, { useCallback, useEffect, useState } from 'react';
import { Modal, TextField } from '@ofself/shell';
import { Facts, Note, Tags } from '../../components/nucleus/Inspector';
import { useToast } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { deriveEscrowKey, wrapKeyForEscrow } from '../../utils/fieldEncryption';
import { FormBand } from './selfForm';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { plural } from '../../nucleus/format';

// RECOVERY CODES — one-time codes that reset your password (and, through the
// escrow each carries, recover your encryption key). New codes are shown ONCE:
// when the server first makes them (GET new_codes), after first-login key
// setup (AuthContext pendingRecoveryCodes), or when you replace them here.

const SHOW = 'self:recovery-codes';
// `backup` re-makes the key escrow for these codes; the sheet runs it, and
// runs it again on Retry backup.
const show = (codes, backup = null) => window.dispatchEvent(new CustomEvent(SHOW, { detail: { codes, backup } }));

export function RecoveryBand() {
    const { recoveryCodesHaveEscrow } = useAuth();
    const [state, setState] = useState(null);
    const [replacing, setReplacing] = useState(false);
    const load = useCallback(() => api.getRecoveryCodes().then((r) => setState({
        codes: r?.codes || [], used: r?.used_count || 0, unused: r?.unused_count || 0,
    })).catch(() => setState({ codes: [], used: 0, unused: 0, error: true })), []);
    useEffect(() => { load(); }, [load]);
    if (!state) return null;
    const low = state.unused <= 2;

    return (
        <FormBand
            title="Recovery codes"
            aside={`${state.unused} unused`}
            lead="One-time codes that reset your password if you lose it. Each works once."
            acts={[{ id: 'replace', label: 'Replace codes', danger: true, run: () => setReplacing(true) }]}
        >
            <Facts items={[
                { label: 'Unused', value: String(state.unused), tone: low ? 'warn' : undefined },
                { label: 'Used', value: String(state.used) },
            ]} />
            <Tags items={state.codes.map((c) => ({ key: c.id || c.position, label: `#${c.position} ${c.status === 'used' ? 'used' : 'unused'}` }))} />
            {recoveryCodesHaveEscrow === false && (
                <Note tone="warn">Your codes can reset your password but can’t recover your encrypted data. Replace them to fix that.</Note>
            )}
            {low && <Note tone="warn">You are running low. Replace them before you run out.</Note>}
            <ReplaceCodes open={replacing} onClose={() => setReplacing(false)} onDone={() => { setReplacing(false); load(); }} />
        </FormBand>
    );
}

// Replacing destroys every earlier code, so the server asks for your password.
// When you have an encryption key, the new codes each carry an escrow of it —
// so the key is unlocked FIRST (with that password if this browser hasn't),
// and nothing is replaced if it can't be. The new codes are shown the moment
// they exist; the escrow runs after, on the sheet, where it can be retried.
function ReplaceCodes({ open, onClose, onDone }) {
    const { user, encKeys, unlockEncKeys } = useAuth();
    const toast = useToast();
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [hasKey, setHasKey] = useState(null);
    useEffect(() => {
        if (!open) return;
        setPassword('');
        api.request('/auth/enc-keys').then((r) => setHasKey(!!(r?.enc_public_key && r?.enc_private_key_wrapped))).catch(() => setHasKey(false));
    }, [open]);
    const needsPassword = hasKey && !encKeys?.privateKey;

    const replace = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            let keys = null;
            if (hasKey) {
                keys = encKeys?.privateKey ? encKeys : await unlockEncKeys(password);
                if (!keys?.privateKey) throw new Error('That password didn’t unlock your key. Nothing was replaced.');
            }
            const res = await api.request('/auth/recovery-codes/regenerate', { method: 'POST', body: { current_password: password } });
            const fresh = res?.codes || [];
            const backup = keys && (async () => {
                const material = await api.request('/auth/enc-keys');
                const recovery_key_escrow = await Promise.all(fresh.map(async ({ id, code }) => ({
                    code_id: id,
                    enc_key_escrow: await wrapKeyForEscrow(keys.privateKey, await deriveEscrowKey(code, String(user.id))),
                })));
                await api.request('/auth/enc-keys', {
                    method: 'PUT',
                    body: { enc_public_key: material.enc_public_key, enc_private_key_wrapped: material.enc_private_key_wrapped, recovery_key_escrow },
                });
            });
            show(fresh.map((c) => c.code), backup);
            onDone();
        } catch (err) {
            toast.error(err?.message || 'Could not replace your codes');
        } finally { setBusy(false); }
    };

    return (
        <Modal open={open} onClose={onClose} title="Replace your recovery codes?" lead="Every earlier code stops working at once, used or not. The new ones are shown once." size="md">
            <form className="self-modal-form" onSubmit={replace}>
                <TextField id="self-recovery-password" type="password" autoComplete="current-password" label="Your password" value={password} autoFocus
                    hint={needsPassword ? 'Confirms it’s you, and unlocks your key in this browser so the new codes can recover it.' : 'Confirms it’s you before every earlier code stops working.'}
                    onChange={(e) => setPassword(e.target.value)} />
                <SheetFoot>
                    <FootButton onClick={onClose}>Keep these</FootButton>
                    <FootButton danger type="submit" busy={busy} disabled={hasKey === null || !password}>Replace codes</FootButton>
                </SheetFoot>
            </form>
        </Modal>
    );
}

// The once-only sheet. Mounted on every Self page.
export function RecoveryCodesNotice() {
    const { pendingRecoveryCodes, clearPendingRecoveryCodes } = useAuth();
    const [codes, setCodes] = useState([]);
    const [copied, setCopied] = useState(false);
    // the key backup for codes just replaced: { run, state: 'saving' | 'saved' | 'failed', error }
    const [backup, setBackup] = useState(null);
    const runBackup = useCallback(async (run) => {
        setBackup({ run, state: 'saving' });
        try {
            await run();
            setBackup({ run, state: 'saved' });
        } catch (e) {
            setBackup({ run, state: 'failed', error: e?.message });
        }
    }, []);
    useEffect(() => {
        let live = true;
        api.getRecoveryCodes().then((r) => {
            if (live && Array.isArray(r?.new_codes) && r.new_codes.length) setCodes(r.new_codes);
        }).catch(() => {});
        const onShow = (e) => {
            setCodes(e.detail?.codes || []);
            if (e.detail?.backup) runBackup(e.detail.backup); else setBackup(null);
        };
        window.addEventListener(SHOW, onShow);
        return () => { live = false; window.removeEventListener(SHOW, onShow); };
    }, [runBackup]);
    useEffect(() => {
        if (pendingRecoveryCodes?.length) setCodes((c) => (c.length ? c : pendingRecoveryCodes));
    }, [pendingRecoveryCodes]);

    const done = () => { setCodes([]); setCopied(false); setBackup(null); clearPendingRecoveryCodes?.(); };
    const copy = async () => {
        try { await navigator.clipboard.writeText(codes.join('\n')); setCopied(true); } catch { /* select by hand */ }
    };
    return (
        <Modal open={codes.length > 0} title="Save your recovery codes" lead={`${plural(codes.length, 'code')}, shown once. Keep them somewhere safe and offline.`} size="md" closeButton={false}>
            <div className="self-codes">
                {codes.map((c) => <code key={c} className="self-codes__code">{c}</code>)}
            </div>
            {backup?.state === 'saving' && <Note>Key backup: saving…</Note>}
            {backup?.state === 'saved' && <Note>Key backup: saved for all {plural(codes.length, 'code')}.</Note>}
            {backup?.state === 'failed' && (
                <Note tone="warn">Key backup failed{backup.error ? `: ${backup.error}` : ''}. These codes already reset your password; retry so they can also recover your encrypted data.</Note>
            )}
            <SheetFoot>
                {backup?.state === 'failed' && <FootButton onClick={() => runBackup(backup.run)}>Retry backup</FootButton>}
                <FootButton onClick={copy}>{copied ? 'Copied' : 'Copy all'}</FootButton>
                <FootButton primary onClick={done}>I saved these</FootButton>
            </SheetFoot>
        </Modal>
    );
}
